import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import FormComp from "../../../Componentes/FormComp";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";
import { GoTrash } from "react-icons/go";

/** Ajuste aqui se o PHP usar outro path ou método. */
const URL_DELETE_NF = (id) => `/Painel/API/ControleMedicao/DeleteNF/index.php?id=${id}`;
const URL_UPDATE_NF = "/Painel/API/ControleMedicao/UpdateNF/index.php";
const URL_ADICIONAR_NF = "/Painel/API/ControleMedicao/AdicionarNF/index.php";

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    color: #333;
    font-size: 12px;
    margin-top: 12px;
    & thead {
        background-color: #f0f0f0;
    }
    & thead th {
        text-align: left;
        padding: 8px 10px;
        font-weight: 600;
    }
    & tbody td {
        padding: 8px 10px;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        vertical-align: top;
    }
`;
const Lixeira = styled(GoTrash)`
    height: 20px;
    width: 20px;
    color: #e11d48;
    cursor: pointer;
    margin-top: 6px;
    &:hover {
        color: #be123c;
    }
`;
const Resumo = styled.div`
    margin-top: 14px;
    padding: 12px;
    border-radius: 8px;
    background: ${(props) => (props.ok ? "#ecfdf5" : "#fef2f2")};
    color: #334155;
    font-size: 13px;
`;
const Vazio = styled.p`
    margin: 16px 0 0 0;
    text-align: center;
    color: #64748b;
    font-size: 14px;
`;
const Titulo = styled.h2`
    margin: 0 0 15px 0;
    font-size: 18px;
    color: #333;
    text-align: center;
`;
const Subtitulo = styled.p`
    margin: 0;
    color: #555;
    text-align: center;
    font-size: 14px;
`;
const BotoesWrapper = styled.div`
    margin-top: 28px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;
const Botao = styled.button`
    padding: 9px 12px;
    background-color: ${(props) => (props.cancel ? "#e5e7eb" : "#2563eb")};
    color: ${(props) => (props.cancel ? "#333" : "white")};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;
const BlocoLista = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;
const BotaoAdicionar = styled.button`
    margin-top: 10px;
    align-self: flex-end;
    padding: 9px 12px;
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    &:hover {
        background-color: #1e40af;
    }
`;

function toDateInputValue(v) {
    if (v == null || v === "") return "";
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function pickCartaCorrecao(n) {
    if (n == null) return "";
    const raw = n.carta_correção ?? n.carta_correcao ?? n["carta_correção"];
    return toDateInputValue(raw);
}

function normalizeListaPayload(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.nfs)) return data.nfs;
    if (Array.isArray(data.lista)) return data.lista;
    return [];
}

function rowFromApi(n) {
    const num_nf = n.num_nf != null ? String(n.num_nf) : "";
    const rawValor = n.valor;
    const valor =
        typeof rawValor === "number" && !Number.isNaN(rawValor)
            ? rawValor
            : parseInt(String(rawValor ?? 0), 10) || 0;
    const data_emissao = toDateInputValue(n.data_emissao);
    const carta_correção = pickCartaCorrecao(n);
    const _initial = { num_nf, valor, data_emissao, carta_correção };
    return {
        id: n.id,
        boletim_id: n.boletim_id,
        num_nf,
        valor,
        data_emissao,
        carta_correção,
        _initial,
        _isNew: false,
    };
}

function createEmptyNewRow() {
    const id = `new_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const hoje = new Date().toISOString().split("T")[0];
    const empty = { num_nf: "", valor: 0, data_emissao: hoje, carta_correção: "" };
    return {
        id,
        boletim_id: null,
        _isNew: true,
        num_nf: empty.num_nf,
        valor: empty.valor,
        data_emissao: empty.data_emissao,
        carta_correção: empty.carta_correção,
        _initial: { ...empty },
    };
}

function isTempNewId(id) {
    return typeof id === "string" && id.startsWith("new_");
}

function isRowModified(row) {
    const i = row._initial;
    return (
        row.num_nf !== i.num_nf ||
        row.valor !== i.valor ||
        row.data_emissao !== i.data_emissao ||
        String(row.carta_correção || "") !== String(i.carta_correção || "")
    );
}

function isNewRowComplete(row) {
    if (!row._isNew) return true;
    return (
        String(row.num_nf || "").trim() !== "" &&
        row.valor > 0 &&
        String(row.data_emissao || "").trim() !== ""
    );
}

const ModalEditarNFs = ({ bm, fecharModal, fetchData }) => {
    const api = useApi();
    const { id: idPedido } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [nfs, setNfs] = useState([]);
    const [deletedIds, setDeletedIds] = useState([]);
    const [removeConfirmId, setRemoveConfirmId] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!bm?.id) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setDeletedIds([]);

            try {
                const { data } = await api.get(`/Painel/API/ControleMedicao/GetNFDetalhes/index.php?idBM=${bm.id}`);
                if (!cancelled) {
                    setNfs(normalizeListaPayload(data).map(rowFromApi));
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) setNfs([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [bm?.id]);

    const somaNFs = useMemo(
        () => nfs.reduce((acc, r) => acc + (Number.isFinite(r.valor) ? r.valor : 0), 0),
        [nfs]
    );

    const somaOk = somaNFs === bm?.valor;

    const newRowsOk = useMemo(() => nfs.every(isNewRowComplete), [nfs]);

    const hasPendingWork = useMemo(
        () =>
            deletedIds.length > 0 ||
            nfs.some((row) => row._isNew || (!row._isNew && isRowModified(row))),
        [nfs, deletedIds]
    );

    const canSave = somaOk && newRowsOk && !loading && hasPendingWork;

    const updateRow = (idRow, patch) => {
        setNfs((prev) => prev.map((r) => (r.id === idRow ? { ...r, ...patch } : r)));
    };

    const confirmRemoveRow = () => {
        if (removeConfirmId == null) return;
        if (isTempNewId(removeConfirmId)) {
            setNfs((prev) => prev.filter((r) => r.id !== removeConfirmId));
        } else {
            setNfs((prev) => prev.filter((r) => r.id !== removeConfirmId));
            setDeletedIds((prev) => [...prev, removeConfirmId]);
        }
        setRemoveConfirmId(null);
    };

    const handleAddRow = () => {
        setNfs((prev) => [...prev, createEmptyNewRow()]);
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!canSave) {
            if (!newRowsOk) {
                alert("Preencha número, valor (> 0) e data de emissão em cada NF nova.");
            } else if (!somaOk) {
                alert("A soma dos valores das NFs deve ser igual ao valor da medição do boletim.");
            } else if (!hasPendingWork) {
                alert("Não há alterações para salvar.");
            }
            return;
        }

        setSaving(true);

        try {
            for (const id of deletedIds) {
                if (isTempNewId(id)) continue;
                await api.post(URL_DELETE_NF(id));
            }

            for (const row of nfs) {
                if (row._isNew || !isRowModified(row)) continue;
                const carta = String(row.carta_correção || "").trim();
                const { data } = await api.patch(URL_UPDATE_NF, {
                    id: row.id,
                    num_nf: row.num_nf,
                    valor: row.valor,
                    data_emissao: row.data_emissao || null,
                    carta_correção: carta || null,
                });
                console.log(data);
            }

            let addIdx = 0;
            for (const row of nfs) {
                if (!row._isNew) continue;
                const formData = new FormData();
                formData.append("idPedido", idPedido ?? "");
                formData.append("idBM", bm.id);
                formData.append("dataEmissaoNF", row.data_emissao || "");
                const cartaNova = String(row.carta_correção || "").trim();
                formData.append(
                    "listaNF",
                    JSON.stringify([
                        {
                            numNF: String(row.num_nf).trim(),
                            valorNF: row.valor,
                            id: Date.now() + addIdx,
                            ...(cartaNova ? { cartaCorrecao: cartaNova } : {}),
                        },
                    ])
                );
                addIdx += 1;
                const { data } = await api.post(URL_ADICIONAR_NF, formData);

                console.log(data);

            }

            await fetchData();
            fecharModal();
        } catch (err) {
            console.error(err);
            alert("Não foi possível salvar. Verifique o console ou tente novamente.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Overlay state={true}>
                <Modal header close={fecharModal} size="lg">
                    <Formulario onSubmit={handleSave}>
                        <FormComp
                            title="NÚMERO BM"
                            type="text"
                            readOnly
                            valor={bm?.num_medicao != null ? String(bm.num_medicao) : ""}
                        />
                        <FormComp
                            title="VALOR DO BOLETIM (MEDIÇÃO)"
                            type="text"
                            readOnly
                            valor={formatBRLFromCents(bm?.valor ?? 0)}
                        />
                        {loading ? (
                            <Vazio>CARREGANDO NFs...</Vazio>
                        ) : (
                            <BlocoLista>
                                {nfs.length === 0 ? (
                                    <Vazio>
                                        Nenhuma NF cadastrada. Use o botão abaixo para incluir uma linha.
                                    </Vazio>
                                ) : (
                                    <Tabela>
                                        <thead>
                                            <tr>
                                                <th>Nº NF</th>
                                                <th>VALOR</th>
                                                <th>EMISSÃO</th>
                                                <th>CARTA CORREÇÃO</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nfs.map((row) => {
                                                return (
                                                    <tr key={row.id}>
                                                        <td>
                                                            <FormComp
                                                                title=""
                                                                type="text"
                                                                valor={row.num_nf}
                                                                changeFunc={(e) =>
                                                                    updateRow(row.id, {
                                                                        num_nf: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <FormComp
                                                                title=""
                                                                type="text"
                                                                valor={formatBRLFromCents(row.valor)}
                                                                changeFunc={(e) =>
                                                                    updateRow(row.id, {
                                                                        valor: parseCentsFromInput(
                                                                            e.target.value
                                                                        ),
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <FormComp
                                                                title=""
                                                                type="date"
                                                                valor={row.data_emissao}
                                                                changeFunc={(e) =>
                                                                    updateRow(row.id, {
                                                                        data_emissao: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <FormComp
                                                                title=""
                                                                type="date"
                                                                valor={row.carta_correção}
                                                                changeFunc={(e) =>
                                                                    updateRow(row.id, {
                                                                        carta_correção: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <Lixeira
                                                                title={
                                                                    row._isNew
                                                                        ? "Remover linha"
                                                                        : "Remover NF (salvar para confirmar no servidor)"
                                                                }
                                                                onClick={() => setRemoveConfirmId(row.id)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Tabela>
                                )}
                                <BotaoAdicionar type="button" onClick={handleAddRow}>
                                    ADICIONAR NF
                                </BotaoAdicionar>
                            </BlocoLista>
                        )}
                        {!loading && nfs.length > 0 && (
                            <Resumo ok={somaOk}>
                                <strong>Soma das NFs:</strong> {formatBRLFromCents(somaNFs)}
                                {" · "}
                                <strong>Valor do boletim:</strong> {formatBRLFromCents(bm?.valor ?? 0)}
                                {!somaOk && (
                                    <span style={{ display: "block", marginTop: 6, color: "#b91c1c" }}>
                                        Os valores precisam coincidir para salvar.
                                    </span>
                                )}
                            </Resumo>
                        )}
                        <FormComp
                            valor={saving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
                            type="submit"
                            disabled={!canSave || saving}
                        />
                    </Formulario>
                </Modal>
            </Overlay>
            {removeConfirmId != null && (
                <Overlay state={true}>
                    <Modal size="sm">
                        <Titulo>Remover NF</Titulo>
                        <Subtitulo>
                            {removeConfirmId != null && isTempNewId(removeConfirmId)
                                ? "Deseja remover esta linha?"
                                : "A nota será excluída do servidor ao salvar. Deseja removê-la desta lista?"}
                        </Subtitulo>
                        <BotoesWrapper>
                            <Botao cancel type="button" onClick={() => setRemoveConfirmId(null)}>
                                CANCELAR
                            </Botao>
                            <Botao type="button" onClick={confirmRemoveRow}>
                                REMOVER
                            </Botao>
                        </BotoesWrapper>
                    </Modal>
                </Overlay>
            )}
        </>
    );
};

export default ModalEditarNFs;